import { memo, useState } from 'react';
import { PUBLIC_ASSETS } from '@/utils/public-assets';
import { Star } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { useGetListCASTCustomerSubmissionQuery } from '@/features/customers/customer-survey/api/get-csat-customer-submission.api';
import { Spinner } from '@/components/_ui/spinner';
import { useSendCSATCustomerSubmission } from '@/features/customers/customer-survey/api/send-csat-customer-submission.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { AxiosErrorCustom, axiosExtractErrorMessage } from '@/lib/axios';
import { APP_ROUTES } from '@/app/app.route.constants';

/**
 * StarRating - Interactive star rating component with hover effects
 */
type StarRatingProps = {
  value?: number;
  onChange?: (rating: number) => void;
};

const StarRating = memo(({ value = 0, onChange }: StarRatingProps) => {
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleClick = (rating: number) => {
    onChange?.(rating);
  };

  return (
    <div className="flex justify-center gap-[8px]" onMouseLeave={() => setHoveredRating(0)}>
      {[1, 2, 3, 4, 5].map((rating) => {
        const isHighlighted = rating <= (hoveredRating || value);
        return (
          <button
            key={rating}
            type="button"
            className="p-[4px] hover:scale-110 transition-transform cursor-pointer"
            onMouseEnter={() => setHoveredRating(rating)}
            onClick={() => handleClick(rating)}
          >
            <Star
              className={`w-[38px] h-[38px] transition-colors ${
                isHighlighted ? 'text-[#FFB800] fill-[#FFB800]' : 'text-[#D9D9D9]'
              }`}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
});

StarRating.displayName = 'StarRating';

const CsatPage = () => {
  const { caseId } = useParams();
  const [formData, setFormData] = useState<Record<string, number | string>>({});
  const showToast = useCustomToast();
  const navigate = useNavigate();

  // Fetch CSAT questions from API
  const {
    data: questionsResponse,
    isLoading,
    isError,
  } = useGetListCASTCustomerSubmissionQuery({
    params: { caseId: caseId || '' },
    queryConfig: {
      enabled: !!caseId,
    },
    select: (data) => data.payload,
  });

  const questionsRaw = questionsResponse || [];
  // Sort by detail so order matches CSAT preview modal (rating first, then text; same relative order)
  const questions = [...questionsRaw].sort((a, b) =>
    (a.detail ?? '').localeCompare(b.detail ?? '', undefined, { sensitivity: 'base' })
  );

  // Validate that all questions have valid, non-empty answers
  const isFormValid = () => {
    if (questions.length === 0) return false;

    return questions.every((question) => {
      const rawValue = formData[question.csatQuestionId];

      if (question.type === 'rating') {
        // Rating must be a number greater than 0
        return typeof rawValue === 'number' && rawValue > 0;
      }

      if (question.type === 'text') {
        // Text must be a non-empty string (after trimming)
        return typeof rawValue === 'string' && rawValue.trim().length > 0;
      }

      return false;
    });
  };

  const { mutate: sendCSATCustomerSubmission, isPending: isSubmitting } = useSendCSATCustomerSubmission({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error as AxiosErrorCustom) ||
          'Failed to send CSAT customer submission, please try again later!';

        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        showToast({
          type: 'success',
          message: 'CSAT customer submission sent successfully!',
        });

        navigate(APP_ROUTES.csat.successCSATPage.getHref());
      },
    },
  });

  // Handle rating change
  const handleRatingChange = (questionId: string, rating: number) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: rating,
    }));
  };

  // Handle text change
  const handleTextChange = (questionId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!caseId) return;

    // Validate form before submission
    if (!isFormValid()) {
      showToast({
        type: 'error',
        message: 'Please answer all questions before submitting.',
      });
      return;
    }

    const csatAnswers =
      questions?.map((question) => {
        const rawValue = formData[question.csatQuestionId];

        // If rating question, send the raw rating value as string
        if (question.type === 'rating') {
          return {
            csatQuestionId: question.csatQuestionId,
            answer: typeof rawValue === 'number' ? String(rawValue) : '',
          };
        }

        // For text question, send the text as-is (string)
        return {
          csatQuestionId: question.csatQuestionId,
          answer: typeof rawValue === 'string' ? rawValue : '',
        };
      }) || [];

    sendCSATCustomerSubmission({
      caseId,
      csatAnswers,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className="w-full h-full min-h-screen bg-cover bg-center bg-no-repeat bg-fixed pt-[80px] pb-[40px]"
        style={{
          backgroundImage: `url(${PUBLIC_ASSETS.PNG.BG_CSAT})`,
        }}
      >
        <div className="border border-[#FFF] rounded-[8px] border-t-[10px] border-t-[#0C8DBF] p-[24px] bg-[#FFF] md:max-w-[400px] md:mx-auto mx-4 max-w-full">
          <div className="flex items-center justify-center gap-[12px]">
            <Spinner className="h-[24px] w-[24px]" />
            <span className="text-[14px] text-[#25151E]">Loading questions...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !caseId) {
    return (
      <div
        className="w-full h-full min-h-screen bg-cover bg-center bg-no-repeat bg-fixed pt-[80px] pb-[40px]"
        style={{
          backgroundImage: `url(${PUBLIC_ASSETS.PNG.BG_CSAT})`,
        }}
      >
        <div className="border border-[#FFF] rounded-[8px] border-t-[10px] border-t-[#0C8DBF] p-[24px] bg-[#FFF] md:max-w-[400px] md:mx-auto mx-4 max-w-full">
          <div className="text-center text-[14px] text-[#FF0000]">
            {!caseId ? 'Invalid case ID' : 'Failed to load questions. Please try again later.'}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (questions.length === 0) {
    return (
      <div
        className="w-full h-full min-h-screen bg-cover bg-center bg-no-repeat bg-fixed pt-[80px] pb-[40px]"
        style={{
          backgroundImage: `url(${PUBLIC_ASSETS.PNG.BG_CSAT})`,
        }}
      >
        <div className="border border-[#FFF] rounded-[8px] border-t-[10px] border-t-[#0C8DBF] p-[24px] bg-[#FFF] md:max-w-[400px] md:mx-auto mx-4 max-w-full">
          <div className="text-center text-[12px] text-[#9D9D9C] py-4">No questions configured</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full min-h-screen bg-cover bg-center bg-no-repeat bg-fixed pt-[80px] pb-[40px]"
      style={{
        backgroundImage: `url(${PUBLIC_ASSETS.PNG.BG_CSAT})`,
      }}
    >
      <div className="border border-[#FFF] rounded-[8px] border-t-[10px] border-t-[#0C8DBF] p-[24px] bg-[#FFF] md:max-w-[400px] md:mx-auto mx-4 max-w-full">
        <div>
          <h3 className="text-[16px] font-[700] text-center">Thank you for contacting the NoLimit Indonesia.</h3>
        </div>

        {/* Questions List — rating (stars) first, then text (like CSAT preview modal) */}
        <div className="flex flex-col gap-[24px] mt-[16px]">
          {questions.length === 0 ? (
            <div className="text-center text-[12px] text-[#9D9D9C] py-4">No questions configured</div>
          ) : (
            <>
              {/* Rating questions first */}
              {questions
                .filter((q) => q.type === 'rating')
                .map((question) => (
                  <div key={question.csatQuestionId} className="flex flex-col gap-[8px]">
                    <div className="text-start">
                      <h3 className="text-[12px] font-[400] text-[#25151E]">{question.detail}</h3>
                    </div>
                    <StarRating
                      value={formData[question.csatQuestionId] as number | undefined}
                      onChange={(rating) => handleRatingChange(question.csatQuestionId, rating)}
                    />
                  </div>
                ))}
              {/* Text questions second */}
              {questions
                .filter((q) => q.type === 'text')
                .map((question) => (
                  <div key={question.csatQuestionId} className="flex flex-col gap-[8px]">
                    <div className="text-start">
                      <h3 className="text-[12px] font-[400] text-[#25151E]">{question.detail}</h3>
                    </div>
                    <textarea
                      placeholder="Type your answer here..."
                      value={(formData[question.csatQuestionId] as string) || ''}
                      onChange={(e) => handleTextChange(question.csatQuestionId, e.target.value)}
                      className="w-full h-[80px] p-[12px] border border-[#D9D9D9] rounded-[8px] text-[12px] resize-none focus:outline-none focus:border-[#0C8DBF] bg-white"
                    />
                  </div>
                ))}
            </>
          )}
        </div>

        {/* Submit Button */}
        <div className="mt-[24px]">
          <button
            onClick={handleSubmit}
            type="button"
            disabled={isSubmitting || !isFormValid()}
            className="w-full py-[10px] bg-[#0C8DBF] text-white text-[14px] font-[400] rounded-[8px] cursor-pointer hover:bg-[#0C8DBF]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CsatPage;
